import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './main.css'
import { initializeTheme } from './services/themeService'

const applyUIMode = () => {
  if (typeof document === 'undefined') return

  // "fast" mode reduces expensive visual effects (blur/animations/shadows) on low-end devices.
  // Override any time via: localStorage.setItem('anna_ui_mode','fancy'|'fast'); location.reload()
  const KEY = 'anna_ui_mode'
  try {
    const stored = localStorage.getItem(KEY)
    const mode = stored === 'fancy' || stored === 'fast' ? stored : 'fast'
    document.documentElement.dataset.ui = mode
  } catch {
    document.documentElement.dataset.ui = 'fast'
  }
}

const cleanupLegacyServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) return

  const isDev = typeof import.meta !== 'undefined' && !!import.meta.env?.DEV
  if (isDev) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      if (registrations.length > 0) {
        await Promise.all(registrations.map((registration) => registration.unregister()))
      }
      if ('caches' in window) {
        const cacheKeys = await caches.keys()
        if (cacheKeys.length > 0) {
          await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)))
        }
      }
    } catch (error) {
      console.warn('Failed to cleanup dev service workers:', error)
    }
    return
  }

  // One-time production cleanup to flush stale cached chunks after recent
  // preview/deploy fixes. Use a versioned flag so it runs only once.
  const FLAG_KEY = 'hosting_sw_cleanup_done_v3'
  try {
    if (localStorage.getItem(FLAG_KEY)) return

    const registrations = await navigator.serviceWorker.getRegistrations()
    const targetRegistrations = registrations.filter((registration) => {
      const scriptUrl = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL || ''
      return scriptUrl.includes('/sw.js') || scriptUrl.includes('workbox')
    })

    if (targetRegistrations.length > 0) {
      await Promise.all(targetRegistrations.map((registration) => registration.unregister()))
    }

    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      if (cacheKeys.length > 0) {
        await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)))
      }
    }

    localStorage.setItem(FLAG_KEY, '1')

    if (targetRegistrations.length > 0) {
      window.location.reload()
    }
  } catch (error) {
    console.warn('Failed to cleanup hosted service workers:', error)
  }
}

applyUIMode()
cleanupLegacyServiceWorkers()

// Initialize festival theme on app start
initializeTheme()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
